import React, { JSX } from 'react';
import { Table } from '@tanstack/react-table';
import { Button, Col, Row } from 'reactstrap';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const Pagination = ({ table }: { table: Table<any> }) => {
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  const renderPageButtons = () => {
    if (pageCount > 3) {
      return (
        <div className="d-flex justify-content-start flex-row">
          <Button color="primary" onClick={() => table.setPageIndex(0)}>
            <FaArrowLeft />
          </Button>
          {Array.from({ length: 3 }, (_, i) => (
            <Button color="primary" key={i} onClick={() => table.setPageIndex(i)}>
              {i + 1}
            </Button>
          ))}
          <Button color="primary" disabled>
            ...
          </Button>
          <Button color="primary" onClick={() => table.setPageIndex(pageCount - 1)}>
            {pageCount}
          </Button>
          <Button color="primary" onClick={() => table.nextPage()}>
            <FaArrowRight />
          </Button>
        </div>
      );
    }

    return Array.from({ length: pageCount }, (_, i) => (
      <Button color="primary" key={i} onClick={() => table.setPageIndex(i)}>
        {i + 1}
      </Button>
    ));
  };

  const renderPageNumbers = () => {
    const pageNumbers: JSX.Element[] = [];
    const DOTS = '...';
    const SIBLINGS = 1; // Number of siblings on each side of current page

    // Always show first page
    pageNumbers.push(
      <li key={1} className={`page-item ${1 === currentPage ? 'active' : ''}`}>
        <button className="page-link" onClick={() => table.setPageIndex(0)}>
          1
        </button>
      </li>,
    );

    if (currentPage > SIBLINGS + 2) {
      pageNumbers.push(
        <li key="dots-1" className="page-item disabled">
          <span className="page-link">{DOTS}</span>
        </li>,
      );
    }

    // Calculate range around current page
    const startPage = Math.max(2, currentPage - SIBLINGS);
    const endPage = Math.min(pageCount - 1, currentPage + SIBLINGS);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button className="page-link" onClick={() => table.setPageIndex(i)}>
            {i}
          </button>
        </li>,
      );
    }

    if (currentPage < pageCount - (SIBLINGS + 1)) {
      pageNumbers.push(
        <li key="dots-2" className="page-item disabled">
          <span className="page-link">{DOTS}</span>
        </li>,
      );
    }

    // Always show last page if there is more than one page
    if (pageCount > 1) {
      pageNumbers.push(
        <li key={pageCount} className={`page-item ${pageCount === currentPage ? 'active' : ''}`}>
          <button className="page-link" onClick={() => table.setPageIndex(pageCount - 1)}>
            {pageCount}
          </button>
        </li>,
      );
    }

    return pageNumbers;
  };

  if (pageCount <= 1) {
    return null;
  }

  return (
    <Row className="g-3 text-sm-start my-2 text-center align-middle">
      {pageCount > 0 && (
        <Col sm="auto" className="mt-0">
          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
            {currentPage > 1 && (
              <li className="page-item">
                <button className="page-link" onClick={() => table.previousPage()}>
                  Previous
                </button>
              </li>
            )}
            {renderPageNumbers()}
            {currentPage < pageCount && (
              <li className="page-item">
                <button className="page-link" onClick={table.nextPage}>
                  Next
                </button>
              </li>
            )}
          </ul>
        </Col>
      )}
    </Row>
  );
};

export default Pagination;
